<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import BaseButton from "@/Components/UI/buttons/BaseButton.vue";
import copyToClipboard from "@/helpers/copyToClipboard.js";

defineProps({
    left: {
        type: String,
        default: 'lg:left-64'
    }
});
const image = computed(() => {
    return type.value === 'success' ? '/storage/images/notify-success.svg' : '/storage/images/notify.svg';
});
const cloudClass = computed(() => {
    switch (type.value) {
        case 'success':
            return 'bg-blue-1';
        case 'error':
            return 'bg-accent-red';
        default:
            return 'bg-dark-2';
    }
});
const type = ref(null);
const text = ref(null);
const show = ref(false);
const action = reactive({
    type: null,
    data: null,
    text: '',
});
const visible = ref(false);
let messages = [];
const notify = (message) => {
    if (!message) {
        return;
    }
    messages.push(message);
    checkMessages();
};

onMounted(() => {
    messages = JSON.parse(sessionStorage.getItem('notification-messages') || '[]');
    checkMessages();
})

const checkMessages = () => {
    sessionStorage.setItem('notification-messages', JSON.stringify(messages));

    if (!messages.length) {
        text.value = '';
        type.value = '';
        visible.value = false;
        return;
    }

    if (visible.value) {
        return;
    }

    let message = messages[0];
    text.value = wrapLinks(escapeHTMLTags(message.text));
    type.value = message.type;
    action.text = message?.action?.text;
    action.data = message?.action?.data;
    action.type = message?.action?.type;
    
    visible.value = true;
    const hasAction = action.type !== null;

    setTimeout(() => {
        show.value = true;
        setTimeout(() => messages.splice(0, 1), hasAction ? 1600 : 800);

        setTimeout(() => {
            show.value = false;

            setTimeout(() => {
                visible.value = false;
                checkMessages();
            }, 500);

        }, hasAction ? 4000 : 2000);

    }, 100)
}

defineExpose({notify});

const handleAction = () => {
    if (!action.type) return;
    
    switch (action.type) {
        case 'copy':
            copyToClipboard(action.data);
            action.text = 'Скопировано';
            break;
        case 'link':
            if (action.data.startsWith('http')) {
                window.open(action.data, '_blank');
            } else {
                window.location.href = action.data;
            }
            break;
        default:
            console.warn('Unknown action type:', action.type);
    }
};

function escapeHTMLTags(html) {
    const escape = document.createElement('textarea');
    escape.textContent = html;
    const res = escape.innerHTML;
    escape.remove();
    return res;
}
function escapeHTMLAttribute(value) {
    // `wrapLinks` runs after `escapeHTMLTags`, so `<`, `>` and `&` are already
    // encoded in `value`. Only `"`/`'` can still break out of the href quotes.
    return value
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
function wrapLinks(content) {
    return content?.replaceAll(/https?:\/\/\S+/g, (url) => {
        const safeHref = escapeHTMLAttribute(url);
        return `<a href="${safeHref}" class="underline" target="_blank">${url}</a>`;
    });
}
</script>

<template>
    <div v-show="visible" ref="flash" class="fixed max-w-sm md:max-w-md bottom-5 transition-all opacity-0 duration-500 px-2 md:pl-10"
         :class="[left, show ? 'bottom-5 opacity-100' : '', visible ? 'z-100' : '-z-100']">
        <div class="flex gap-10">
            <img :src="image" class="h-64 w-44" alt="" width="85" height="124">
            <div class="relative">
                <p class="relative top-0 origin-bottom-left -translate-y-full px-7 py-4 rounded text-white" :class="cloudClass">
                    <svg class="absolute -bottom-1 -left-1" width="12" height="10" viewBox="0 0 12 10"
                         xmlns="http://www.w3.org/2000/svg">
                        <path v-if="type === 'success'" class="fill-blue-1" d="M1.21189 9.18719C1.14352 9.24771 1.04759 9.26615 0.961662 9.23528C0.875731 9.20441 0.813463 9.12915 0.799233 9.03896C0.630648 7.97046 0.909004 6.88522 1.41688 5.87232C1.92513 4.85868 2.67129 3.90085 3.46565 3.07653C4.26062 2.25157 5.11095 1.55301 5.83539 1.05907C6.19741 0.812235 6.53167 0.613984 6.81485 0.47633C7.08613 0.344456 7.34617 0.25 7.54618 0.25C7.63165 0.25 7.7112 0.293669 7.75709 0.365781L11.2571 5.86578C11.3061 5.94276 11.3092 6.04032 11.2653 6.1203C11.2214 6.20029 11.1374 6.25 11.0462 6.25C10.699 6.25 10.3558 6.24379 10.0142 6.23761C8.8324 6.21622 7.67034 6.1952 6.43376 6.433C4.85576 6.73646 3.1576 7.46481 1.21189 9.18719Z"/>
                        <path v-else-if="type === 'error'" class="fill-accent-red" d="M1.21189 9.18719C1.14352 9.24771 1.04759 9.26615 0.961662 9.23528C0.875731 9.20441 0.813463 9.12915 0.799233 9.03896C0.630648 7.97046 0.909004 6.88522 1.41688 5.87232C1.92513 4.85868 2.67129 3.90085 3.46565 3.07653C4.26062 2.25157 5.11095 1.55301 5.83539 1.05907C6.19741 0.812235 6.53167 0.613984 6.81485 0.47633C7.08613 0.344456 7.34617 0.25 7.54618 0.25C7.63165 0.25 7.7112 0.293669 7.75709 0.365781L11.2571 5.86578C11.3061 5.94276 11.3092 6.04032 11.2653 6.1203C11.2214 6.20029 11.1374 6.25 11.0462 6.25C10.699 6.25 10.3558 6.24379 10.0142 6.23761C8.8324 6.21622 7.67034 6.1952 6.43376 6.433C4.85576 6.73646 3.1576 7.46481 1.21189 9.18719Z"/>
                        <path v-else class="fill-dark-2" d="M1.21189 9.18719C1.14352 9.24771 1.04759 9.26615 0.961662 9.23528C0.875731 9.20441 0.813463 9.12915 0.799233 9.03896C0.630648 7.97046 0.909004 6.88522 1.41688 5.87232C1.92513 4.85868 2.67129 3.90085 3.46565 3.07653C4.26062 2.25157 5.11095 1.55301 5.83539 1.05907C6.19741 0.812235 6.53167 0.613984 6.81485 0.47633C7.08613 0.344456 7.34617 0.25 7.54618 0.25C7.63165 0.25 7.7112 0.293669 7.75709 0.365781L11.2571 5.86578C11.3061 5.94276 11.3092 6.04032 11.2653 6.1203C11.2214 6.20029 11.1374 6.25 11.0462 6.25C10.699 6.25 10.3558 6.24379 10.0142 6.23761C8.8324 6.21622 7.67034 6.1952 6.43376 6.433C4.85576 6.73646 3.1576 7.46481 1.21189 9.18719Z"/>
                    </svg>
                    <span v-html="text"/>
                    <BaseButton variant="secondary"
                                v-if="action?.text" @click="handleAction"
                                class="mt-2 text-xs border-light-2 border transition-all duration-300 hover:bg-light-2 hover:text-accent-blue">
                        {{ action.text }}
                    </BaseButton>
                </p>
            </div>
        </div>
    </div>
</template>
